import { CaseUser, UserRole } from "./caseUser"
import { FieldDataTypes } from "@/types/fieldTypes"

// Data Types are for the underlying values contained within fields.
// Multiple fields can match to the same DataType, for example, both number and dollar both map to number

// See SVCases.Models.Search.DataTypes in the data service for a matching definition
export enum DataType {
	string = 0,
	dateTime = 1,
	number = 2,
	boolean = 3,
	array = 4,
	arrayMultiple = 5,
	arrayData = 6,
	calculated = 7,
	none = 8,
	userRole = 9,
	mapPoint = 10,
	bounds = 11,
	timeSpentAsValue = 12,
	team = 13,
	dateRange = 14,
	subrole = 15,
	auditUser = 16,
}

// New Data Type checklist:
// - Add to enum (Above)
// - Add to SVCases.Models.Search.DataTypes enum in Data Service
// - Add grouping function to groupingFunctions (below) (If relevant)
// - Add operators to dataTypeOperations in @/types/operators.ts
// - Add to dataTypeTests in @/types/operators.ts
// - Add to DataTypeFunctions in @/scripts/dataTypeFunctions.ts

// Function for grouping by data types
export const groupingFunctions: { [key in DataType]?: (record: any, groupByValue: any, cache: { [key:string]: any }, groupByOption?: string | null) => { key: string, label: string, value: any }[]} = {
	[DataType.userRole]: (record: any, groupByValue: UserRole, cache: { [key:string]: any }, groupByOption) => {
		const mapUsers: (users: CaseUser[]) => { key: string, label: string, value: any }[] =
			users => users.map(user => {
				const userValue = cache[user.id]
					? cache[user.id]
					: cache[user.id] = record.users.find((u: CaseUser) => u.id === user.id)
				return {
					key: user.id,
					label: userValue ? userValue.name : user.id,
					value: userValue
				}
			})

		let returnValues = groupByValue ? [
			...(groupByValue.users ? mapUsers(groupByValue.users) : []),
			...(groupByValue.subRoles
				? groupByValue.subRoles.map(role => role.users ? mapUsers(role.users) : []).flat()
				: [])
		] : []

		if (groupByOption === "team") {
			returnValues = returnValues.filter(u => u.value && u.value.teams)
				.map(u => u.value.teams.map((team: any) => {
					return {
						key: team.id,
						label: team.title,
						value: team
					}
				}))
				.flat()
		}

		return returnValues.length === 0
			? [
				{
					key: "none",
					label: "None set",
					value: null
				}
			]
			: returnValues
	}
}

export const groupByOptions: { [key in DataType]?: {value: string, label: string}[]} = {
	[DataType.userRole]: [
		{ value: "user", label: "User" },
		{ value: "team", label: "Role" }
	]
}

export const groupingOptions = {
	subRole: "subRole"
}

export const dataTypeByFieldType: { [k: string]: DataType } = {
	[FieldDataTypes.Text]: DataType.string,
	[FieldDataTypes.TextArea]: DataType.string,
	[FieldDataTypes.Number]: DataType.number,
	[FieldDataTypes.Dollar]: DataType.number,
	[FieldDataTypes.Date]: DataType.dateTime,
	[FieldDataTypes.DateTime]: DataType.dateTime,
	[FieldDataTypes.Checkbox]: DataType.boolean,
	[FieldDataTypes.List]: DataType.array,
	[FieldDataTypes.ListMultiple]: DataType.arrayMultiple,
	[FieldDataTypes.ListData]: DataType.arrayData,
	[FieldDataTypes.UserRole]: DataType.userRole,
	[FieldDataTypes.MapMarker]: DataType.mapPoint,
	[FieldDataTypes.Calculated]: DataType.number,
	[FieldDataTypes.ExternalLink]: DataType.string,
	[FieldDataTypes.CaseNumber]: DataType.string,
	[FieldDataTypes.TimeSpent]: DataType.timeSpentAsValue,
	[FieldDataTypes.FileOnly]: DataType.none
}

export const dataTypeParameters = [
	{
		label: "Text",
		dataType: DataType.string,
		selectSource: false,
		fieldDataTypes: [DataType.string]
	},
	{
		label: "Number",
		dataType: DataType.number,
		selectSource: false,
		fieldDataTypes: [DataType.number]
	},
	{
		label: "Date",
		dataType: DataType.dateTime,
		selectSource: false,
		fieldDataTypes: [DataType.dateTime]
	},
	{
		label: "Date Range",
		dataType: DataType.dateRange,
		selectSource: false,
		fieldDataTypes: [DataType.dateTime]
	},
	{
		label: "Data Source",
		dataType: DataType.array,
		selectSource: true,
		fieldDataTypes: [DataType.array, DataType.arrayMultiple, DataType.arrayData]
	},
	{
		label: "User",
		dataType: DataType.userRole,
		selectSource: false,
		fieldDataTypes: [DataType.userRole]
	}
]
